/**
 * OperatorSetupAssistant — a GUIDANCE-ONLY, rule-based setup helper panel for
 * the Operator Provisioning Wizard. No LLM, no network, no secrets: it surfaces
 * curated FAQ answers and per-step starter questions. (Later swappable for a
 * live LLM once a provider + key handling + cost controls are approved.)
 */
import React, { useMemo, useState } from "react";
import { Bot, X, ChevronRight, Search, ShieldCheck } from "lucide-react";
import { FAQ, STEP_STARTERS, UNSURE_REPLY } from "./operatorProvisioningFaq";

export const OperatorSetupAssistant = ({ stepId, open, onClose }) => {
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState("");

  const starters = useMemo(() => (STEP_STARTERS[stepId] || []).filter((id) => FAQ[id]), [stepId]);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const entries = Object.entries(FAQ);
    if (!q) return entries;
    return entries.filter(([, { q: question, a }]) => (question + " " + a).toLowerCase().includes(q));
  }, [query]);

  if (!open) return null;
  const active = activeId ? FAQ[activeId] : null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-white border border-border shadow-2xl flex flex-col max-h-[70vh]" data-testid="operator-setup-assistant">
      <div className="flex items-center justify-between px-4 py-3 bg-[#0A0A0A] text-white">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-[#FFC107]" />
          <span className="font-heading font-black text-sm tracking-tight">Operator Setup Assistant</span>
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="assistant-close"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex items-center gap-1.5 px-4 py-2 bg-[#FFFBEB] border-b border-[#FCD34D] text-[11px] text-[#92400E]">
        <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
        <span>Guidance only — answers are informational. No accounts, secrets or settings are changed.</span>
      </div>

      <div className="overflow-y-auto p-3 space-y-3">
        {active ? (
          <div data-testid="assistant-answer">
            <button onClick={() => setActiveId(null)} className="text-xs text-[#002FA7] hover:underline mb-2" data-testid="assistant-back">← All questions</button>
            <div className="font-bold text-sm mb-1.5">{active.q}</div>
            <div className="text-sm text-foreground whitespace-pre-line leading-relaxed">{active.a}</div>
            <div className="mt-3 pt-2 border-t border-border text-[11px] text-muted-foreground">{UNSURE_REPLY}</div>
          </div>
        ) : (
          <>
            {starters.length > 0 && (
              <div data-testid="assistant-starters">
                <div className="label-mini text-muted-foreground mb-1.5">About this step</div>
                <div className="space-y-1">
                  {starters.map((id) => (
                    <button key={id} onClick={() => setActiveId(id)} className="w-full text-left text-sm px-3 py-2 border border-border hover:bg-secondary flex items-center justify-between gap-2" data-testid={`assistant-starter-${id}`}>
                      <span>{FAQ[id].q}</span><ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="flex items-center border border-border px-2 mb-1.5">
                <Search className="w-3.5 h-3.5 text-muted-foreground" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search setup help…" className="flex-1 px-2 py-1.5 text-sm outline-none" data-testid="assistant-search" />
              </div>
              <div className="label-mini text-muted-foreground mb-1.5">{query ? `${results.length} result(s)` : "All topics"}</div>
              <div className="space-y-1">
                {results.map(([id, { q }]) => (
                  <button key={id} onClick={() => setActiveId(id)} className="w-full text-left text-sm px-3 py-2 border border-border hover:bg-secondary flex items-center justify-between gap-2" data-testid={`assistant-faq-${id}`}>
                    <span>{q}</span><ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OperatorSetupAssistant;
